import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { CameraFeed } from "./CameraFeed";
import {
	FaceAuthCorrectableErrorCodes,
	FaceAuthErrorCode,
	MultipleMatchesDetails,
	MultipleMatchesDetailsSchema,
} from "@/api/face-auth";
import { useToast } from "@/components/ui/use-toast";
import ErrorPage from "@/components/ui/ErrorPage";
import { authService } from "@/services/auth";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button/button";
import VerifyPhone from "@/pages/Home/card/view/_components/VerifyPhone";
import useSendPhone2FA from "@/features/phone-verification/hooks/useSendPhone2FA.tsx";
import { CameraOverlay } from "./CameraOverlay";
import { useCamera, ImageQuality, CropAreas } from "@/common/hooks/useCamera";
import useFaceAuth from "@/features/auth/useFaceAuth.ts";

const FaceAuth = () => {
	const navigate = useNavigate();
	const [terminalError, setTerminalError] = useState(false);
	const [showNoMatchDialog, setShowNoMatchDialog] = useState(false);
	const [showVerifyPhone, setShowVerifyPhone] = useState(false);
	const [multipleMatchesDetails, setMultipleMatchesDetails] =
		useState<MultipleMatchesDetails>();
	const { toast } = useToast();
	const sendPhone2FA = useSendPhone2FA();
	const faceAuth = useFaceAuth();

	const { videoRef, captureImage } = useCamera(() => setTerminalError(true), {
		quality: ImageQuality.high,
		cropArea: CropAreas.none,
		maxWidth: 720,
	});

	const handleVerifyPhoneSubmit = (phone: string, countryCode: number) => {
		sendPhone2FA.mutate(
			{
				payload: {
					phone_number: phone,
					global_country_id: countryCode,
				},
			},
			{
				onSuccess: () => {
					const candidateIds =
						multipleMatchesDetails?.candidates.map(
							(candidate) => candidate.identity,
						) ?? undefined;
					navigate({
						to: "/verify-code",
						search: {
							phone,
							country: Number(countryCode),
							log_id: multipleMatchesDetails?.log_id,
							candidate_ids: candidateIds,
						},
					});
				},
				onError: () => {
					setShowNoMatchDialog(true);
				},
			},
		);
	};

	const processImage = (imageBlob: Blob | null) => {
		if (!imageBlob) return;

		const request = { imageBlob };
		faceAuth.mutate(request, {
			onSuccess: (responseBody) => {
				authService.setToken(responseBody);
				navigate({ to: "/main/home" });
			},
			onError: (error) => {
				if (
					error.code === FaceAuthErrorCode.new_user_or_unverified_user_detected
				) {
					setShowNoMatchDialog(true);
					return;
				}

				if (error.code === FaceAuthErrorCode.multiple_matches) {
					const parseResult = MultipleMatchesDetailsSchema.safeParse(
						error.details,
					);
					if (parseResult.success) {
						setMultipleMatchesDetails(parseResult.data);
						setShowVerifyPhone(true);
						return;
					}
					setTerminalError(true);
					return;
				}

				if (FaceAuthCorrectableErrorCodes.includes(error.code)) {
					toast({
						title: "Try again",
						description: error.message,
						variant: "destructive",
					});
					return;
				}

				setTerminalError(true);
			},
		});
	};

	const handleCapture = async () => {
		try {
			const blob = await captureImage();
			processImage(blob);
		} catch (error) {
			console.error("Capture failed:", error);
			toast({
				title: "Capture failed",
				description: "Could not capture image. Please try again.",
				variant: "destructive",
			});
		}
	};

	if (terminalError) {
		return (
			<ErrorPage
				message="Device camera access failed"
				submitText="Log in with Email"
				onClose={() => navigate({ to: "/face-id" })}
				onSubmit={() => navigate({ to: "/sign-mail" })}
			/>
		);
	}

	return (
		<>
			{showVerifyPhone ? (
				<VerifyPhone
					onSubmit={handleVerifyPhoneSubmit}
					submitMutationStatus={sendPhone2FA.status}
				/>
			) : (
				<CameraOverlay
					isLoading={faceAuth.isPending}
					onBackClick={() => navigate({ to: "/face-id" })}
					onCaptureClick={handleCapture}
				>
					<CameraFeed videoRef={videoRef} />
				</CameraOverlay>
			)}

			{/* No-match decision modal */}
			<Dialog open={showNoMatchDialog} onOpenChange={setShowNoMatchDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>We couldn't find you</DialogTitle>
						<DialogDescription>
							Either you're new here or your account isn't verified yet. Pick an
							option below to continue.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="flex gap-2 sm:justify-end">
						<Button
							size="md"
							variant="outline"
							onClick={() => {
								setShowNoMatchDialog(false);
								navigate({
									to: "/sign-up",
									search: {
										promo: undefined,
									},
								});
							}}
						>
							New here? Sign up
						</Button>
						<Button
							size="md"
							variant="default"
							onClick={() => {
								setShowNoMatchDialog(false);
								navigate({ to: "/sign-mail" });
							}}
						>
							Not verified? Sign in
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default FaceAuth;
